import { useContext, useEffect, useState } from 'react';
import { batch, useDispatch } from 'react-redux';

import { MediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import { useGuideInfo } from '@/hooks/app/useGuideInfo';
import { useThemeState } from '@/hooks/store';
import { setGuide } from '@/store/guide/actions';
import { Guide } from '@/store/guide/types';
import { setProgram } from '@/store/program/actions';
import { Program } from '@/store/program/types';
import { setTheme } from '@/store/theme/actions';

interface UseInitializeGuide {
	(guide: Guide, program: Program): {};
}

export const useInitializeGuide: UseInitializeGuide = (guide, program) => {
	const theme = useThemeState();
	const dispatch = useDispatch();
	const { image } = useGuideInfo(guide, program);
	const { onAudioStart, onVideoStart, playerMode } = useContext(MediaPlayerContext);

	const [hasInitialized, setHasInitialized] = useState(false);

	useEffect(() => {
		const setBackgroundImage = () => {
			dispatch(
				setTheme({
					...theme,
					webAppBackgroundImage: image,
				}),
			);
		};

		const playContent = () => {
			if (!guide || !program) {
				return;
			}

			if (playerMode !== 'playing' && program?.meditation_type !== 'timer') {
				batch(() => {
					dispatch(setGuide(guide));
					dispatch(setProgram(program));
				});

				if (guide.asset.type === 'audio') {
					onAudioStart(null, guide, program);
				} else if (guide.asset.type === 'video') {
					onVideoStart(guide, program, 'paused', false);
				}
			}
		};

		if (!hasInitialized && program && guide) {
			setBackgroundImage();
			playContent();
			setHasInitialized(true);
		}
	}, [dispatch, guide, onAudioStart, onVideoStart, playerMode, program, theme, image, hasInitialized]);

	return {
		image,
	};
};
